import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';

interface ImageRendererProps {
  url?: string;
  file?: File;
  filename?: string;
  mode?: 'preview' | 'thumbnail' | 'dialog';
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: (error?: any) => void;
  onClick?: () => void;
  onZoomControlsChange?: (controls: { zoomIn: () => void; zoomOut: () => void; resetTransform: () => void }) => void;
}

export const ImageRenderer: React.FC<ImageRendererProps> = React.memo(({
  url,
  file,
  filename,
  mode = 'preview',
  width = '100%',
  height = '100%',
  onLoad,
  onError,
  onClick,
  onZoomControlsChange
}) => {
  const [zoomControls, setZoomControls] = useState<{ zoomIn: () => void; zoomOut: () => void; resetTransform: () => void } | null>(null);
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);

  // Resolve the image source (file or URL) - prioritize URL to prevent memory leaks
  const imageSource = useMemo(() => {
    // If URL is provided, use it directly - don't create additional blob URLs
    if (url) {
      return url;
    }
    // Only create blob URL if no URL was provided
    if (file) {
      return URL.createObjectURL(file);
    }
    return undefined;
  }, [url, file]);

  console.log('[ImageRenderer] Rendering with source:', { url, file: file?.name, resolved: imageSource });

  // Cleanup blob URL when component unmounts or file changes
  // Only cleanup if we created the blob URL ourselves (when no url was provided)
  useEffect(() => {
    return () => {
      if (!url && file && imageSource && imageSource.startsWith('blob:')) {
        URL.revokeObjectURL(imageSource);
      }
    };
  }, [url, file, imageSource]);

  // Early return if no image source provided
  if (!imageSource) {
    return (
      <Box sx={{ 
        width, 
        height, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.1)',
        borderRadius: 1
      }}>
        <Typography color="text.secondary">No image source provided</Typography>
      </Box>
    );
  }

  // Thumbnail mode
  if (mode === 'thumbnail') {
    return (
      <Box 
        sx={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: onClick ? 'pointer' : 'default',
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 1,
          overflow: 'hidden'
        }}
        onClick={onClick}
      >
        <img
          src={imageSource}
          alt={filename || 'Image'}
          onLoad={onLoad}
          onError={onError}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
      </Box>
    );
  }

  // Full image with zoom controls
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        bgcolor: 'transparent',
      }}
    >
      <TransformWrapper
        ref={transformComponentRef}
        initialScale={1}
        initialPositionX={0}
        initialPositionY={0}
        minScale={0.1}
        maxScale={20}
        centerOnInit={true}
        limitToBounds={false}
        alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
        velocityAnimation={{ sensitivity: 0 }}
        wheel={{ disabled: false, step: 0.1 }}
        pinch={{ disabled: false }}
        doubleClick={{ disabled: false }}
        centerZoomedOut={true}
        panning={{ 
          disabled: false,
          velocityDisabled: true,
          allowLeftClickPan: true,
          lockAxisX: false,
          lockAxisY: false
        }}
        onInit={() => {
          // Set up zoom controls when transform wrapper initializes
          if (transformComponentRef.current) {
            const { zoomIn, zoomOut, resetTransform } = transformComponentRef.current;
            const controls = { zoomIn, zoomOut, resetTransform };
            setZoomControls(controls);
            
            // Pass zoom controls to parent if callback provided
            if (onZoomControlsChange) {
              onZoomControlsChange(controls);
            }
          }
        }}
      >
        {(utils) => (
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              maxHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
            contentStyle={{
              width: 'auto',
              height: 'auto',
              maxHeight: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={imageSource}
              alt={filename || 'Image'}
              onClick={onClick}
              onLoad={onLoad}
              onError={onError}
              style={{
                maxHeight: '75vh',
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                userSelect: 'none',
                display: 'block',
                cursor: onClick ? 'pointer' : 'default'
              }}
            />
          </TransformComponent>
        )}
      </TransformWrapper>
    </Box>
  );
});

export default ImageRenderer; 